import React, { useCallback, useEffect, useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupString,
  YupStringReq,
  isNumeric,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { FormInput, gqlDeleteCntItem, gqlGetCntItemById, gqlUpdateCntItem } from '../graphql';
import { FormFields } from './form-fields';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { RowFieldTooltips } from 'shared-pages/components';
import { getRouteByName } from 'app/routes';
import { useRootStore } from 'store';

/** Φόρμα επεξεργασίας είδους εισφοράς. Το ιστορικό τιμών μένει προβολή. */
export const CntItemFormPage = () => {
  const { handleGraphqlError } = useHandleError('CntItem');
  const { appStore } = useRootStore();
  const history = useHistory();
  const location: any = useLocation();
  const { id } = useParams<{ id: string }>();
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const { t } = useTranslation();
  const canEdit = appStore.isPlatformOwner;

  const formShape = {
    id: YupNumeric,
    code: YupString,
    name: YupStringReq,
    enName: YupString,
    fullName: YupString,
    footnote: YupString,
    enFootnote: YupString,
    groupId: YupNumeric,
    subgroupId: YupNumeric,
    units1: YupString,
    units2: YupString,
    units3: YupString,
    priceCalculationMethod: YupNumeric,
    empaCategory: YupString,
    enEmpaCategory: YupString,
    erpItemCode: YupString,
    erpItemName: YupString,
    enErpItemName: YupString,
    hasDetails: Yup.boolean().nullable(),
    useInEmpa: Yup.boolean().nullable(),
    useInEdoe: Yup.boolean().nullable(),
    useInEdoeUsed: Yup.boolean().nullable(),
    useInNonEdoe: Yup.boolean().nullable(),
    isActive: Yup.boolean().nullable(),
  };

  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));

  const [updateRecord] = useMutation(gqlUpdateCntItem);
  const [deleteRecord] = useMutation(gqlDeleteCntItem);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetCntItemById);
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;

  const formValues = useMemo(() => {
    if (!data) return undefined;
    // Στη βάση το κενό ERP αποθηκεύεται ως το κείμενο "null"
    const erpItemCode =
      data.erpItemCode == null || data.erpItemCode === 'null' ? '' : data.erpItemCode;
    return {
      id: data.id,
      code: data.code ?? '',
      name: data.name ?? '',
      enName: data.enName ?? '',
      fullName: data.fullName ?? '',
      footnote: data.footnote ?? '',
      enFootnote: data.enFootnote ?? '',
      groupId: data.groupId ?? data.group?.id ?? null,
      subgroupId: data.subgroupId ?? data.subgroup?.id ?? null,
      units1: data.units1 ?? '',
      units2: data.units2 ?? '',
      units3: data.units3 ?? '',
      priceCalculationMethod: data.priceCalculationMethod ?? null,
      empaCategory: data.empaCategory ?? '',
      enEmpaCategory: data.enEmpaCategory ?? '',
      erpItemCode,
      // Η ονομασία ERP έρχεται από το query αλλά δεν έμπαινε στα αρχικά values
      erpItemName: data.erpItemName ?? '',
      enErpItemName: data.enErpItemName ?? '',
      hasDetails: data.hasDetails ?? null,
      useInEmpa: data.useInEmpa ?? null,
      useInEdoe: data.useInEdoe ?? null,
      useInEdoeUsed: data.useInEdoeUsed ?? null,
      useInNonEdoe: data.useInNonEdoe ?? null,
      isActive: data.isActive ?? null,
    };
  }, [data]);

  useEffect(() => {
    if (idNum > 0) get({ variables: { id: idNum } });
  }, [get, idNum]);

  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('CntItems')), { ...location.state });
  }, [history, location.state]);

  const handleSubmit = async (values, actions) => {
    actions.setSubmitting(false);
    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
    // Οι στήλες ERP δεν δέχονται null
    mutValues.erpItemCode = mutValues.erpItemCode ?? '';
    mutValues.erpItemName = mutValues.erpItemName ?? '';
    mutValues.enErpItemName = mutValues.enErpItemName ?? '';
    updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
      .then((d) => {
        toastSuccess(`${t('Επιτυχής Ενημέρωση Εγγραφής')} [${d.data[Object.keys(d.data)[0]].id}]`);
        refetch();
      })
      .catch((err) => {
        handleGraphqlError('mutation-update', err);
      });
  };

  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: t('Διαγραφή Εγγραφής'),
      text: t('Είστε σίγουροι ότι θέλετε να διαγράψετε το είδος εισφοράς;'),
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => {
          toastSuccess(`${t('Διαγράφηκε η εγγραφή')} [${idNum}]`);
          handleReturn();
        })
        .catch((err) => {
          handleGraphqlError('mutation-delete', err);
        });
    }
  }, [deleteRecord, handleGraphqlError, handleReturn, idNum, t]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  }

  if (!data || !formValues) {
    return <h3 className="text-center">{t('Παρακαλώ περιμένετε...')}</h3>;
  }

  const titleName = [data.code, data.name].filter(Boolean).join(' — ');

  return (
    <FormCard
      title={`${t('Είδος Εισφοράς')}: ${titleName}`}
      onSubmit={handleSubmit}
      onDelete={canEdit ? handleDelete : undefined}
      onReturn={handleReturn}
      permitSubmit={!canEdit}
      permitDelete={canEdit}
      initialValues={pickProperties(formValues)}
      validationSchema={validationSchema}
      enableReinitialize
      idx={idNum}
    >
      <FormFields data={data} canEdit={canEdit} />
      {appStore.isPlatformOwner && (
        <RowFieldTooltips data={data} showLegacy={false} auditTable="cnt_items" />
      )}
    </FormCard>
  );
};
